// ====================================================
// VidyaTrack — Admin: Classes & Sections
// ====================================================

import React, { useState, useEffect } from "react";
import { FiPlus, FiTrash2, FiEdit2, FiUsers } from "react-icons/fi";
import { useData } from "../../contexts/DataContext";
import Modal from "../../components/shared/Modal";
import { toast } from "react-toastify";

const CLASS_NAMES = ["Class 1","Class 2","Class 3","Class 4","Class 5",
  "Class 6","Class 7","Class 8","Class 9","Class 10","Class 11","Class 12"];
const SECTIONS = ["A","B","C","D","E"];
const COLORS   = ["#054bb4","#059669","#7c3aed","#d97706","#dc2626","#0284c7","#0891b2","#be185d"];

export default function ClassesPage() {
  const { getClasses, addClass, updateClass, deleteClass, getTeachers } = useData();

  const [classes,   setClasses]   = useState([]);
  const [teachers,  setTeachers]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [errors,    setErrors]    = useState({});
  const [form, setForm] = useState({
    name: "", section: "", teacherId: "", teacherName: "", room: "", capacity: 40,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [cl, tc] = await Promise.all([getClasses(), getTeachers()]);
      setClasses(cl);
      setTeachers(tc);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ name:"", section:"", teacherId:"", teacherName:"", room:"", capacity:40 });
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (cls) => {
    setEditId(cls.id);
    setForm({
      name:      cls.name      || "",
      section:   cls.section   || "",
      teacherId: cls.teacherId || "",
      teacherName: cls.teacherName || "",
      room:      cls.room      || "",
      capacity:  cls.capacity  || 40,
    });
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = "Class name is required";
    if (!form.section.trim()) e.section = "Section is required";
    return e;
  };

  const handleSave = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const teacher = teachers.find(t => t.id === form.teacherId);
      const payload = {
        ...form,
        teacherName: teacher?.name || form.teacherName || "",
        capacity:    Number(form.capacity) || 40,
      };
      if (editId) {
        await updateClass(editId, payload);
        toast.success("Class updated!");
      } else {
        await addClass({ ...payload, students: 0 });
        toast.success("Class added!");
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error("Failed to save: " + (err.message || "")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this class? This cannot be undone.")) return;
    try { await deleteClass(id); toast.success("Class deleted!"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const f = (key, val) => {
    setForm(p => ({ ...p, [key]: val }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: "" }));
  };

  return (
    <div className="animate-fade">
      <div className="page-head">
        <div>
          <h1 className="page-title">🏫 Classes & Sections</h1>
          <p className="page-subtitle">{classes.length} {classes.length === 1 ? "class" : "classes"} configured</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <FiPlus size={15} /> Add Class
        </button>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : classes.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon">🏫</div>
            <div className="empty-title">No classes yet</div>
            <div className="empty-desc">Add your first class to get started.</div>
          </div>
        </div>
      ) : (
        <div className="g g3">
          {classes.map((cls, i) => {
            const color = COLORS[i % COLORS.length];
            const pct   = cls.capacity > 0 ? Math.min(Math.round(((cls.students || 0) / cls.capacity) * 100), 100) : 0;
            return (
              <div key={cls.id} className="card">
                <div style={{ height:4, background:color }} />
                <div style={{ padding:"16px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                    <div>
                      <h3 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"1rem", color, lineHeight:1 }}>
                        {cls.name} — {cls.section}
                      </h3>
                      {cls.room && <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginTop:4 }}>Room: {cls.room}</div>}
                    </div>
                    <div style={{ display:"flex", gap:6 }}>
                      <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openEdit(cls)} title="Edit">
                        <FiEdit2 size={13} />
                      </button>
                      <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(cls.id)} title="Delete">
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </div>

                  <div style={{ fontSize:".82rem", color:"var(--gray-700)", marginBottom:6 }}>
                    <div style={{ marginBottom:4, display:"flex", alignItems:"center", gap:6 }}>
                      <span>👩‍🏫</span>
                      <span>{cls.teacherName || <em style={{ color:"var(--gray-400)" }}>No teacher assigned</em>}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <FiUsers size={13} style={{ color:"var(--gray-400)" }} />
                      <span>{cls.students || 0} student{cls.students !== 1 ? "s" : ""} / {cls.capacity || 40} capacity</span>
                    </div>
                  </div>

                  <div style={{ marginTop:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:".72rem", color:"var(--gray-500)", marginBottom:4 }}>
                      <span>Capacity</span>
                      <span style={{ fontWeight:600 }}>{pct}%</span>
                    </div>
                    <div className="progress">
                      <div className={`progress-bar ${pct >= 90 ? "red" : pct >= 70 ? "amber" : "green"}`} style={{ width:`${pct}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editId ? "Edit Class" : "Add New Class"}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><div className="spinner spinner-sm" /> Saving…</> : editId ? "Update" : "Add Class"}
            </button>
          </>
        }
      >
        <div className="g g2">
          <div className="form-group">
            <label className="form-label">Class Name *</label>
            <select className={`form-control${errors.name ? " error" : ""}`} value={form.name} onChange={e => f("name", e.target.value)}>
              <option value="">Select class</option>
              {CLASS_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            {errors.name && <div className="form-error">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Section *</label>
            <select className={`form-control${errors.section ? " error" : ""}`} value={form.section} onChange={e => f("section", e.target.value)}>
              <option value="">Select section</option>
              {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.section && <div className="form-error">{errors.section}</div>}
          </div>

          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Assign Teacher</label>
            <select className="form-control" value={form.teacherId} onChange={e => f("teacherId", e.target.value)}>
              <option value="">— Not assigned —</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.name}{t.subject ? ` (${t.subject})` : ""}</option>
              ))}
            </select>
            {teachers.length === 0 && (
              <div className="form-hint">No teachers found. Add teachers first.</div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Room No.</label>
            <input className="form-control" placeholder="e.g. Room 101" value={form.room} onChange={e => f("room", e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Capacity</label>
            <input type="number" className="form-control" min={1} max={100} value={form.capacity}
              onChange={e => f("capacity", e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
